'use client';

import { resolveCookieDomain } from '../config/get-config';
import { browserCookieName, parseBrowserSession, tokenIdentity, type BrowserSession } from './browser-session';

export const AUTH_CHANGED_EVENT = 'pteultra-auth-changed';
const GOOGLE_AUTO_SIGN_IN_SUPPRESSED_KEY = 'google_auto_sign_in_suppressed';
const legacyKeys = ['user_token', 'refresh_token', 'userAuthState', 'user_email'];
const maxAge = 30 * 24 * 60 * 60;
let cleanedLegacy = false;

function domain() {
    return resolveCookieDomain(window.__APP_CONFIG__ ?? null, window.location.hostname);
}
function attributes(remove = false, cookieDomain = domain()) {
    return `; path=/; max-age=${remove ? 0 : maxAge}; samesite=lax`
        + (cookieDomain ? `; domain=${cookieDomain}` : '')
        + (window.location.protocol === 'https:' ? '; secure' : '');
}
function removeCookie(name: string) {
    // Do not clear Production's parent-domain cookies while visiting Dev/Stage.
    for (const cookieDomain of new Set(['', domain()])) document.cookie = `${name}=${attributes(true, cookieDomain)}`;
}
function cleanupLegacy() {
    if (cleanedLegacy) return;
    cleanedLegacy = true;
    for (const name of legacyKeys) {
        removeCookie(name);
        try { window.localStorage.removeItem(name); window.sessionStorage.removeItem(name); } catch { /* Storage may be disabled. */ }
    }
}
export function readBrowserSession(): BrowserSession | null {
    if (typeof document === 'undefined') return null;
    cleanupLegacy();
    const name = browserCookieName(domain());
    const values = document.cookie.split(';').map(v => v.trim()).filter(v => v.startsWith(`${name}=`));
    if (!values.length) return null;
    try {
        if (values.length !== 1) throw new Error('duplicate_session');
        const session = parseBrowserSession(decodeURIComponent(values[0].slice(name.length + 1)));
        if (session) return session;
    } catch { /* Invalid or conflicting cookies are never a login. */ }
    removeCookie(name);
    return null;
}
export function sessionSnapshot() { return JSON.stringify(readBrowserSession()); }
export function hasStoredAuthToken() { return readBrowserSession() !== null; }
export function getStoredAuthToken() { return readBrowserSession()?.accessToken ?? ''; }

function writeSession(session: BrowserSession) {
    const raw = JSON.stringify(session);
    const encoded = encodeURIComponent(raw);
    if (!parseBrowserSession(raw) || encoded.length > 3500) throw new Error('Sign in could not be saved. Please try again.');
    const name = browserCookieName(domain());
    document.cookie = `${name}=${encoded}${attributes()}`;
    if (sessionSnapshot() !== raw) throw new Error('Allow cookies to stay signed in.');
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}
export function persistAuthenticatedUserSession({ accessToken, refreshToken, email = '' }: {
    accessToken?: string | null; refreshToken?: string | null; email?: string | null;
}) {
    if (!accessToken || !refreshToken) throw new Error('Sign in could not be completed.');
    const identity = tokenIdentity(accessToken);
    if (!identity) throw new Error('Sign in could not be completed.');
    cleanupLegacy();
    writeSession({ version: 2, loginId: crypto.randomUUID(), userId: identity.userId,
        accessToken, refreshToken, email: email ?? '' });
    clearGoogleAutoSignInSuppression();
}
export function replaceRefreshedSession(expected: string, accessToken: string, refreshToken: string) {
    if (sessionSnapshot() !== expected) return false;
    const session = readBrowserSession();
    if (!session || tokenIdentity(accessToken)?.userId !== session.userId) return false;
    writeSession({ ...session, accessToken, refreshToken });
    return true;
}
export function isGoogleAutoSignInSuppressed() {
    try { return window.localStorage.getItem(GOOGLE_AUTO_SIGN_IN_SUPPRESSED_KEY) === '1'; } catch { return false; }
}
export function clearGoogleAutoSignInSuppression() {
    try { window.localStorage.removeItem(GOOGLE_AUTO_SIGN_IN_SUPPRESSED_KEY); } catch { /* Optional preference. */ }
}
export function clearStoredAuthTokens(options: { suppressGoogleAutoSignIn?: boolean; expectedSnapshot?: string } = {}) {
    if (typeof window === 'undefined' || (options.expectedSnapshot !== undefined && sessionSnapshot() !== options.expectedSnapshot)) return;
    const hadSession = readBrowserSession() !== null;
    removeCookie(browserCookieName(domain()));
    cleanedLegacy = false;
    cleanupLegacy();
    if (options.suppressGoogleAutoSignIn) {
        try {
            const identity = (window as Window & { google?: { accounts?: { id?: { cancel?: () => void; disableAutoSelect?: () => void } } } }).google?.accounts?.id;
            identity?.cancel?.();
            identity?.disableAutoSelect?.();
        } catch { /* SDK availability cannot block local logout. */ }
        try { window.localStorage.setItem(GOOGLE_AUTO_SIGN_IN_SUPPRESSED_KEY, '1'); } catch { /* Optional preference. */ }
        void window.pteAcquisitionReset?.();
    }
    if (hadSession) window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}
